import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Alert,
  Divider,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  CardContent,
  Card,
  Breadcrumbs,
  Link as MuiLink,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import { usePumpageReportForm } from "./usePumpageReportForm";
import CurrentDroughtAlert from "../../components/droughtDeclaration/CurrentDroughtAlert";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import { ROUTES } from "../../constants";
import PumpageSummary from "../../components/pumpageSummary/PumpageSummary";
import { useApp } from "../../AppProvider";
import { Helmet } from "react-helmet-async";
import { usePumpageBaseline } from "./usePumpageBaseline";
import { usePumpageSummary } from "../../components/pumpageSummary/usePumpageSummary";

const FIELDS = [
  "meter_reading",
  "total_pumped",
  "calculated_pumpage",
  "prev_meter_reading",
  "other_water_srcs",
  "total_sold",
  "total_loss",
  "total_loss_percent",
  "loss_explain",
  "updated_at",
  "updated_by_name",
  "created_at",
  "created_by_name",
];

const DIALOG_ONLY_FIELDS = ["prev_meter_reading"];

const getTableFields = () =>
  FIELDS.filter((field) => !DIALOG_ONLY_FIELDS.includes(field));

const COLUMN_GROUPING_MODEL = [
  {
    groupId: "Q1",
    headerName: "Q1",
    headerAlign: "center",
    children: [{ field: "JAN" }, { field: "FEB" }, { field: "MAR" }],
  },
  {
    groupId: "Q2",
    headerName: "Q2",
    headerAlign: "center",
    children: [{ field: "APR" }, { field: "MAY" }, { field: "JUN" }],
  },
  {
    groupId: "Q3",
    headerName: "Q3",
    headerAlign: "center",
    children: [{ field: "JUL" }, { field: "AUG" }, { field: "SEP" }],
  },
  {
    groupId: "Q4",
    headerName: "Q4",
    headerAlign: "center",
    children: [{ field: "OCT" }, { field: "NOV" }, { field: "DEC" }],
  },
];

const MONTHS = [
  { field: "JAN", group: "Q1" },
  { field: "FEB", group: "Q1" },
  { field: "MAR", group: "Q1" },
  { field: "APR", group: "Q2" },
  { field: "MAY", group: "Q2" },
  { field: "JUN", group: "Q2" },
  { field: "JUL", group: "Q3" },
  { field: "AUG", group: "Q3" },
  { field: "SEP", group: "Q3" },
  { field: "OCT", group: "Q4" },
  { field: "NOV", group: "Q4" },
  { field: "DEC", group: "Q4" },
];

const CURRENT_YEAR = dayjs().year();
const CURRENT_MONTH = dayjs().month() + 1;

function getQuarter(m) {
  if ([1, 2, 3].includes(m)) return 1;
  if ([4, 5, 6].includes(m)) return 2;
  if ([7, 8, 9].includes(m)) return 3;
  return 4;
}

function getDefaultVisibleGroups(selectedYear) {
  // If viewing historical data (any year before current year), show all quarters
  if (selectedYear < CURRENT_YEAR) {
    return {
      Q1: true,
      Q2: true,
      Q3: true,
      Q4: true,
    };
  }

  // For current year, only show quarters up to current quarter
  const currentQuarter = getQuarter(CURRENT_MONTH);
  return {
    Q1: currentQuarter >= 1,
    Q2: currentQuarter >= 2,
    Q3: currentQuarter >= 3,
    Q4: currentQuarter >= 4,
  };
}

/**
 * Helper to generate an array of years [2000 ... currentYear+1].
 */
function getYearOptions() {
  const years = [];
  for (let y = 2000; y <= CURRENT_YEAR + 1; y++) {
    years.push(y);
  }
  return years;
}

const isFutureMonth = (field, year) => {
  // Historical years are never future
  if (year < CURRENT_YEAR) return false;

  const idx = MONTHS.findIndex((m) => m.field === field) + 1;
  return (year === CURRENT_YEAR && idx > CURRENT_MONTH) || year > CURRENT_YEAR;
};

function toNumericSafe(value) {
  if (typeof value === "number") return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}

function monthIndexToLabel(monthIndex) {
  return MONTHS[monthIndex - 1].field;
}

const MONTH_NAMES = {
  JAN: "January",
  FEB: "February",
  MAR: "March",
  APR: "April",
  MAY: "May",
  JUN: "June",
  JUL: "July",
  AUG: "August",
  SEP: "September",
  OCT: "October",
  NOV: "November",
  DEC: "December",
};

function renderMeasureName(params) {
  const names = {
    meter_reading: "Meter Reading",
    total_pumped: "Pumped Totals (Legacy)",
    calculated_pumpage: "Calculated Pumpage",
    prev_meter_reading: "Previous Meter Reading",
    other_water_srcs: "Other Sources",
    total_sold: "Sold/Used",
    total_loss: "Loss",
    total_loss_percent: "Loss (% of gallons pumped)",
    loss_explain: "Loss Explanation",
    updated_at: "Last Updated",
    updated_by_name: "Updated By Name",
    created_at: "Created At",
    created_by_name: "Created By Name",
  };
  return names[params.value] || params.value;
}

function renderPumpageCell(params) {
  if (
    params.row.rowKey === "total_loss_percent" &&
    params.value !== undefined &&
    params.value !== null &&
    params.value !== ""
  ) {
    return `${params.value}%`;
  }

  if (
    ["updated_at", "created_at"].includes(params.row.rowKey) &&
    params.value &&
    dayjs(params.value).isValid()
  ) {
    return (
      <div
        style={{
          lineHeight: "1.2",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <div style={{ margin: 0, padding: 0 }}>
          {dayjs(params.value).format("MM-DD-YYYY")}
        </div>
        <div style={{ margin: 0, padding: 0 }}>
          {dayjs(params.value).format("HH:mm")}
        </div>
      </div>
    );
  }

  // Format numeric values with commas (except percentages and non-numeric fields)
  if (
    params.value !== undefined &&
    params.value !== null &&
    params.value !== "" &&
    !isNaN(params.value) &&
    ![
      "total_loss_percent",
      "updated_at",
      "created_at",
      "updated_by_name",
      "created_by_name",
      "loss_explain",
    ].includes(params.row.rowKey)
  ) {
    return Number(params.value).toLocaleString();
  }

  return params.value;
}

export function getPumpageColumns(
  months,
  onEditClick,
  isFutureMonth,
  selectedYear,
  hoveredColumn,
  setHoveredColumn,
  isRecordLocked
) {
  return [
    {
      field: "rowKey",
      headerName: "Measure",
      sortable: false,
      width: 180,
      renderCell: renderMeasureName,
      headerAlign: "center",
    },
    ...months.map(({ field }) => ({
      field,
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      renderCell: (params) => {
        const isLocked = isRecordLocked(field);
        const isFuture = isFutureMonth(field, selectedYear);

        return (
          <Box
            onMouseEnter={() =>
              !isLocked && !isFuture && setHoveredColumn(field)
            }
            onMouseLeave={() => setHoveredColumn(null)}
            onClick={() => onEditClick(field)}
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              cursor: isLocked || isFuture ? "not-allowed" : "pointer",
            }}
          >
            {renderPumpageCell(params)}
          </Box>
        );
      },
      renderHeader: () => {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              height: "100%",
              width: "100%",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
              {field}
            </Typography>
            {!isRecordLocked(field) && !isFutureMonth(field, selectedYear) && (
              <Button
                size="small"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick(field);
                }}
                sx={{
                  width: "100%",
                  fontSize: "0.75rem",
                  py: 0.25,
                  px: 1,
                }}
              >
                Edit
              </Button>
            )}
          </Box>
        );
      },
    })),
  ];
}

function pivotPumpageEntries(entries) {
  const pivot = Object.fromEntries(FIELDS.map((f) => [f, {}]));

  entries.forEach((e) => {
    const monthLabel = monthIndexToLabel(e.rpt_month);
    FIELDS.forEach((field) => {
      pivot[field][monthLabel] = {
        value: e[field],
        id: e.pumpage_ndx,
      };
    });
  });

  // Only return rows for fields that should be displayed in the table
  return getTableFields().map((field) => {
    const newRow = { id: field, rowKey: field };
    Object.entries(pivot[field]).forEach(([month, { value, id }]) => {
      newRow[month] = value;
      newRow[`${month}_id`] = id;
    });
    return newRow;
  });
}

function BaselineDialog({
  open,
  onClose,
  permitNdx,
  prevPumpageInfo, // { value: number, year: number, month: number } | null
  onSaved,
}) {
  const [reading, setReading] = useState("");
  const { save } = usePumpageBaseline(permitNdx);

  const handleSave = async () => {
    if (prevPumpageInfo) {
      // Has previous pumpage data - use those dates
      await save.mutateAsync({
        permit_ndx: permitNdx,
        year: prevPumpageInfo.year,
        month: prevPumpageInfo.month,
        reading: Number(reading),
      });
    } else {
      // No previous pumpage data - this is a new permit, save with null dates
      await save.mutateAsync({
        permit_ndx: permitNdx,
        year: null,
        month: null,
        reading: Number(reading),
      });
    }
    onSaved();
    onClose();
  };

  const isFirstTimeEntry = !prevPumpageInfo;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Enter Baseline Meter Reading</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {isFirstTimeEntry ? (
          <Typography sx={{ mb: 2 }}>
            This appears to be your first pumpage entry for this permit. Please
            enter your current meter reading to establish a baseline for future
            calculations.
          </Typography>
        ) : (
          <Typography sx={{ mb: 2 }}>
            We haven't seen any meter readings for this permit yet. Please enter
            the meter value that corresponded to your last reported pumpage (
            {prevPumpageInfo.value.toLocaleString()} gallons in{" "}
            {MONTHS[prevPumpageInfo.month - 1].field} {prevPumpageInfo.year}).
          </Typography>
        )}

        <TextField
          label="Baseline meter reading"
          type="number"
          value={reading}
          onChange={(e) => setReading(e.target.value)}
          autoFocus
          fullWidth
          helperText={
            isFirstTimeEntry
              ? "Enter your current meter reading in gallons"
              : "Enter the meter reading that corresponds to your last pumpage report"
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!reading}>
          Save Baseline
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PumpageReportForm() {
  const apiRef = useGridApiRef();
  const { doToast, currentUser } = useApp();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const selectedPermitNdx = parseInt(queryParams.get("permit_ndx"), 10) || null;
  const selectedDroughtZoneNdx =
    parseInt(queryParams.get("drought_zone_ndx"), 10) || null;
  const selectedYear = parseInt(queryParams.get("year"), 10) || dayjs().year();

  const [visibleGroups, setVisibleGroups] = useState(() =>
    getDefaultVisibleGroups(selectedYear)
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const { query: baselineQuery } = usePumpageBaseline(selectedPermitNdx);
  const [baselineDlgOpen, setBaselineDlgOpen] = useState(false);
  const [baselineDismissed, setBaselineDismissed] = useState(false);
  const pumpageSummaryRefetchRef = useRef(null);

  const {
    permittedEntitiesQuery,
    pumpageEntriesQuery,
    saveSingleMonthMutation,
  } = usePumpageReportForm(selectedPermitNdx, selectedYear);

  // Import the summary data for latest pumpage info
  const { data: summaryData } = usePumpageSummary(
    selectedPermitNdx,
    selectedYear
  );

  const latestPrevPumpageInfo = useMemo(() => {
    if (!summaryData) return null;

    // Use the new summary fields for latest pumpage
    if (
      summaryData.latest_pumpage !== null &&
      summaryData.latest_pumpage !== undefined &&
      summaryData.latest_pumpage_rpt_month !== null &&
      summaryData.latest_pumpage_rpt_month !== undefined &&
      summaryData.latest_pumpage_rpt_year !== null &&
      summaryData.latest_pumpage_rpt_year !== undefined
    ) {
      return {
        value: summaryData.latest_pumpage,
        year: summaryData.latest_pumpage_rpt_year,
        month: summaryData.latest_pumpage_rpt_month,
      };
    }

    return null;
  }, [summaryData]);

  useEffect(() => {
    if (
      !pumpageEntriesQuery.isLoading &&
      selectedPermitNdx &&
      baselineQuery.data === null &&
      !baselineDlgOpen &&
      !baselineDismissed
    ) {
      setBaselineDlgOpen(true);
    }
  }, [
    pumpageEntriesQuery.isLoading,
    baselineQuery.data,
    selectedPermitNdx,
    baselineDlgOpen,
    baselineDismissed,
  ]);

  // Determine if we're in legacy mode for a given month/year
  const isLegacyMode = useCallback(
    (year, month) => {
      if (!baselineQuery.data) return true;
      const baselineYear = baselineQuery.data.baseline_rpt_year;
      const baselineMonth = baselineQuery.data.baseline_rpt_month;

      // If baseline dates are null, this is a new permit (not legacy)
      if (baselineYear === null || baselineMonth === null) return false;

      // If the record is before or on the baseline date, it's legacy mode
      return (
        year < baselineYear || (year === baselineYear && month <= baselineMonth)
      );
    },
    [baselineQuery.data]
  );

  // Determine if a record is locked (has pumpage_ndx and user is not a developer)
  const isRecordLocked = useCallback(
    (month) => {
      if (currentUser?.isDeveloper) return false; // Developers can always edit

      const monthIndex = MONTHS.findIndex((m) => m.field === month) + 1;
      const monthRecord = pumpageEntriesQuery.data?.find(
        (entry) => entry.rpt_month === monthIndex
      );
      // Record is locked if it has a pumpage_ndx (has been saved)
      return monthRecord?.pumpage_ndx != null;
    },
    [currentUser?.isDeveloper, pumpageEntriesQuery.data]
  );

  const handleEditClick = useCallback(
    (month) => {
      // Prevent editing locked records
      if (isRecordLocked(month)) {
        doToast(
          "info",
          "This record has been submitted and is locked. Please contact the district to make changes."
        );
        return;
      }

      setSelectedMonth(month);

      // Find the individual record for this month from the original pumpage entries data
      const monthIndex = MONTHS.findIndex((m) => m.field === month) + 1;
      const monthRecord = pumpageEntriesQuery.data?.find(
        (entry) => entry.rpt_month === monthIndex
      );

      if (monthRecord) {
        setEditingValues({
          pumpage_ndx: monthRecord.pumpage_ndx,
          meter_reading:
            monthRecord.meter_reading !== null &&
            monthRecord.meter_reading !== undefined
              ? monthRecord.meter_reading
              : "",
          total_pumped:
            monthRecord.total_pumped !== null &&
            monthRecord.total_pumped !== undefined
              ? monthRecord.total_pumped
              : "",
          prev_meter_reading: monthRecord.prev_meter_reading,
          prev_pumpage: monthRecord.prev_pumpage,
          other_water_srcs:
            monthRecord.other_water_srcs !== null &&
            monthRecord.other_water_srcs !== undefined
              ? monthRecord.other_water_srcs
              : "",
          total_sold:
            monthRecord.total_sold !== null &&
            monthRecord.total_sold !== undefined
              ? monthRecord.total_sold
              : "",
          loss_explain: monthRecord.loss_explain || "",
          rpt_month: monthIndex,
          rpt_year: selectedYear,
        });
      }

      setEditDialogOpen(true);
    },
    [pumpageEntriesQuery.data, selectedYear, isRecordLocked, doToast]
  );

  const BASE_COLUMNS = useMemo(
    () =>
      getPumpageColumns(
        MONTHS,
        handleEditClick,
        isFutureMonth,
        selectedYear,
        hoveredColumn,
        setHoveredColumn,
        isRecordLocked
      ),
    [handleEditClick, selectedYear, hoveredColumn, isRecordLocked]
  );

  const rows = useMemo(
    () =>
      pumpageEntriesQuery.data
        ? pivotPumpageEntries(pumpageEntriesQuery.data)
        : [],
    [pumpageEntriesQuery.data]
  );

  const columnVisibilityModel = useMemo(
    () =>
      MONTHS.reduce(
        (acc, { field, group }) => ({
          ...acc,
          [field]: visibleGroups[group] && !isFutureMonth(field, selectedYear),
        }),
        { rowKey: true }
      ),
    [visibleGroups, selectedYear]
  );

  const setSearchParams = (newParams) => {
    const updatedParams = new URLSearchParams(location.search);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) updatedParams.set(key, value);
      else updatedParams.delete(key);
    });
    history.push({ search: updatedParams.toString() });
  };

  const handlePermitChange = (_, newValue) => {
    if (newValue) {
      setSearchParams({
        permit_ndx: newValue.permit_ndx,
        drought_zone_ndx: newValue.assoc_drought_zone_ndx,
      });
    } else {
      setSearchParams({ permit_ndx: "", drought_zone_ndx: "" });
    }
    // Reset baseline dismissed flag when permit changes
    setBaselineDismissed(false);
  };

  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value, 10);
    setSearchParams({ year: newYear });
    setVisibleGroups(getDefaultVisibleGroups(newYear));
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedMonth(null);
    setEditingValues({});
  };

  // Simple pumpage calculation: current meter reading - previous meter reading
  const calculateCurrentPumpage = useCallback(
    (currentMeterReading, prevMeterReading) => {
      if (!currentMeterReading && currentMeterReading !== 0) return 0;
      if (!prevMeterReading && prevMeterReading !== 0) return 0;

      return Math.max(
        0,
        Number(currentMeterReading) - Number(prevMeterReading)
      );
    },
    []
  );

  const handleSaveDialogData = async () => {
    // Validate integer inputs
    for (const [field, value] of Object.entries(editingValues)) {
      if (
        field !== "loss_explain" &&
        value !== "" &&
        !Number.isInteger(Number(value))
      ) {
        doToast(
          "error",
          `Invalid input in ${renderMeasureName({
            value: field,
          })}: Must be an integer.`
        );
        return;
      }
    }

    // Validate required fields based on mode
    const isLegacyRecord = isLegacyMode(
      editingValues.rpt_year,
      editingValues.rpt_month
    );
    if (isLegacyRecord) {
      if (!editingValues.total_pumped && editingValues.total_pumped !== 0) {
        doToast("error", "Total Pumped is required.");
        return;
      }
    } else {
      if (!editingValues.meter_reading && editingValues.meter_reading !== 0) {
        doToast("error", "Meter Reading is required.");
        return;
      }
    }

    // Validate meter reading logic only for non-legacy records
    if (!isLegacyRecord) {
      // Validate that current meter reading is not less than previous meter reading
      if (
        editingValues.prev_meter_reading !== null &&
        editingValues.prev_meter_reading !== undefined
      ) {
        const currentReading = Number(editingValues.meter_reading);
        const prevReading = Number(editingValues.prev_meter_reading);

        if (currentReading < prevReading) {
          doToast(
            "error",
            `Current meter reading (${currentReading.toLocaleString()}) cannot be less than the previous reading (${prevReading.toLocaleString()}). If a correction is needed, please contact the district directly.`
          );
          return;
        }
      }
    }

    // Validate that total_sold has an explicit value (including 0)
    if (
      editingValues.total_sold === "" ||
      editingValues.total_sold === null ||
      editingValues.total_sold === undefined
    ) {
      doToast("error", "Sold/Used is required.");
      return;
    }

    // Validate that other_water_srcs has an explicit value (including 0)
    if (
      editingValues.other_water_srcs === "" ||
      editingValues.other_water_srcs === null ||
      editingValues.other_water_srcs === undefined
    ) {
      doToast("error", "Other Water Sources is required");
      return;
    }

    // Calculate pumpage based on mode
    let pumpageAmount;

    if (isLegacyRecord) {
      pumpageAmount = Number(editingValues.total_pumped) || 0;
    } else {
      pumpageAmount = calculateCurrentPumpage(
        editingValues.meter_reading,
        editingValues.prev_meter_reading
      );
    }

    const other = Number(editingValues.other_water_srcs) || 0;
    const sold = Number(editingValues.total_sold);
    const totalAvailable = pumpageAmount + other;
    const loss = totalAvailable - sold;
    const lossPercent =
      totalAvailable === 0
        ? 0
        : Math.round((loss / totalAvailable) * 100 * 100) / 100;

    // Validate no negative losses
    if (loss < 0 || lossPercent < 0) {
      doToast(
        "error",
        "Loss or Loss (% of gallons pumped) cannot be negative."
      );
      return;
    }

    // Validate loss explanation is required when Loss (% of gallons pumped) > 15%
    if (
      lossPercent > 15 &&
      (!editingValues.loss_explain || editingValues.loss_explain.trim() === "")
    ) {
      doToast(
        "error",
        "Loss explanation is required when loss exceeds 15%. Please provide a meaningful explanation, not just spaces."
      );
      return;
    }

    // Get the month index from the selected month
    const monthIndex = MONTHS.findIndex((m) => m.field === selectedMonth) + 1;

    // Prepare the single month entry
    const monthEntry = {
      permit_ndx: selectedPermitNdx,
      rpt_year: selectedYear,
      rpt_month: monthIndex,
      rpt_qtr: getQuarter(monthIndex),
      pumpage_ndx: editingValues.pumpage_ndx || undefined,
      meter_reading: isLegacyRecord
        ? null
        : editingValues.meter_reading || null,
      total_pumped: isLegacyRecord
        ? toNumericSafe(editingValues.total_pumped) || null
        : null,
      other_water_srcs: toNumericSafe(editingValues.other_water_srcs),
      total_sold: toNumericSafe(editingValues.total_sold),
      total_loss: loss,
      total_loss_percent: lossPercent,
      loss_explain: editingValues.loss_explain || "",
    };

    try {
      await saveSingleMonthMutation.mutateAsync(monthEntry);

      // Refetch data to get the updated values
      await pumpageEntriesQuery.refetch();

      // Trigger summary refetch after successful save
      if (pumpageSummaryRefetchRef.current) {
        pumpageSummaryRefetchRef.current();
      }

      handleCloseDialog();
    } catch (error) {
      console.error("Save error:", error);
    }
  };

  const loading =
    permittedEntitiesQuery.isLoading || pumpageEntriesQuery.isLoading;
  const permitHolderOptions = permittedEntitiesQuery.data || [];
  return (
    <Box>
      <Helmet>
        <title>Pumpage Report</title>
      </Helmet>

      <Typography variant="h3" gutterBottom display="inline">
        Pumpage Report
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Pumpage Report</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      <Box mb={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Pumpage Entry
            </Typography>

            <Card variant="outlined" mb={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instructions
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Permittees are required to report their{" "}
                  <strong>meter readings</strong> on a{" "}
                  <strong>monthly basis</strong>. Click on any month column to
                  enter the current meter reading for that month.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Pumpage amounts</strong> are automatically calculated
                  by subtracting the previous month's meter reading from the
                  current reading.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Future months</strong> are disabled and cannot be
                  filled in advance. You may only enter data for the current
                  month or any past month.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Important:</strong> Data is saved immediately when you
                  click "Save Changes" in the dialog. Each month is saved
                  individually.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Once a month's pumpage record has been submitted, it is
                  locked. If you need to make changes to a submitted month,
                  please <strong>contact the district directly</strong>.
                </Typography>
                <Typography variant="body2">
                  Fields such as <strong>Calculated Pumpage</strong>,{" "}
                  <strong>Loss</strong>, and{" "}
                  <strong>Loss (% of gallons pumped)</strong> are computed
                  automatically based on your meter readings and other entries.
                </Typography>
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 2 }}>
              <Autocomplete
                options={permitHolderOptions}
                getOptionLabel={(option) =>
                  `#${option.permit_ndx} | ${
                    option.name_company || "No Company"
                  } | ${option.address || "No Address"} | ${
                    option.phone_primary || "No Phone"
                  }`
                }
                value={
                  permitHolderOptions.find(
                    (opt) => opt.permit_ndx === selectedPermitNdx
                  ) || null
                }
                onChange={handlePermitChange}
                fullWidth
                sx={{ maxWidth: 500 }}
                renderInput={(params) => (
                  <TextField {...params} label="Permit Holder" />
                )}
              />

              <TextField
                select
                label="Year"
                value={selectedYear}
                onChange={handleYearChange}
                sx={{ width: 120 }}
              >
                {getYearOptions().map((yr) => (
                  <MenuItem key={yr} value={yr}>
                    {yr}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Help & Download Buttons */}

            <Alert
              severity="info"
              variant="outlined"
              sx={{ mb: 3 }}
              icon={<PictureAsPdfIcon fontSize="inherit" />}
            >
              <AlertTitle>How to Read Your Meter</AlertTitle>
              This short guide explains how to correctly read and record your
              pumpage meter readings. Please review it before submitting your
              monthly data.
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  href="/static/pdf/how_to_read_your_meter.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LaunchIcon />}
                >
                  View Guide
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  href="/static/pdf/how_to_read_your_meter.pdf"
                  download
                  startIcon={<DownloadIcon />}
                >
                  Download PDF
                </Button>
              </Box>
            </Alert>

            {selectedPermitNdx && selectedYear ? (
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {Object.keys(visibleGroups)
                    .filter((qtr) => {
                      const quarterNumber = parseInt(qtr.slice(1));
                      // For historical years, show all quarters
                      if (selectedYear < CURRENT_YEAR) {
                        return true;
                      }
                      // For current year, only show quarters up to current quarter
                      return quarterNumber <= getQuarter(CURRENT_MONTH);
                    })
                    .map((qtr) => (
                      <FormControlLabel
                        key={qtr}
                        control={
                          <Checkbox
                            checked={visibleGroups[qtr]}
                            onChange={() =>
                              setVisibleGroups({
                                ...visibleGroups,
                                [qtr]: !visibleGroups[qtr],
                              })
                            }
                          />
                        }
                        label={`Show ${qtr}`}
                      />
                    ))}
                </Box>
                <DataGrid
                  apiRef={apiRef}
                  loading={loading}
                  rows={rows}
                  columns={BASE_COLUMNS}
                  columnVisibilityModel={columnVisibilityModel}
                  columnGroupingModel={COLUMN_GROUPING_MODEL}
                  columnHeaderHeight={80}
                  hideFooter
                  disableRowSelectionOnClick
                  disableColumnSelector
                  disableColumnFilter
                  disableColumnMenu
                  disableDensitySelector
                  sortingOrder={[]}
                  sx={(theme) => ({
                    height: 400,
                    width: "100%",
                    // // Use CSS custom properties for column highlighting
                    // [`--hovered-column-${hoveredColumn}`]: hoveredColumn
                    //   ? "rgba(25, 118, 210, 0.04)"
                    //   : "transparent",
                    "& .MuiDataGrid-cell": {
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      // Remove focus borders
                      "&:focus": {
                        outline: "none",
                      },
                      "&:focus-within": {
                        outline: "none",
                      },
                      // Apply hover background to entire cell including padding
                      ...(hoveredColumn && {
                        [`&[data-field="${hoveredColumn}"]`]: {
                          backgroundColor:
                            "rgba(25, 118, 210, 0.04) !important",
                        },
                      }),
                    },
                    "& .MuiDataGrid-columnHeader": {
                      paddingY: 1,
                      // Remove focus borders from headers
                      "&:focus": {
                        outline: "none",
                      },
                      "&:focus-within": {
                        outline: "none",
                      },
                      // Remove sorting cursor
                      cursor: "default",
                      "& .MuiDataGrid-columnHeaderTitle": {
                        cursor: "default",
                      },
                      // Apply hover background to entire header including padding
                      ...(hoveredColumn && {
                        [`&[data-field="${hoveredColumn}"]`]: {
                          backgroundColor:
                            "rgba(25, 118, 210, 0.04) !important",
                        },
                      }),
                    },
                    // Center quarter headers
                    "& .MuiDataGrid-columnHeadersInner .MuiDataGrid-columnHeadersInner--pinnedLeft .MuiDataGrid-columnHeader":
                      {
                        justifyContent: "center",
                      },
                    "& .MuiDataGrid-columnGroup .MuiDataGrid-columnGroupHeader":
                      {
                        justifyContent: "center",
                      },
                    // Disable row hover
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "transparent !important",
                    },
                    // Remove cell selection styling
                    "& .MuiDataGrid-cell--withRenderer": {
                      "&:focus": {
                        outline: "none",
                      },
                    },
                  })}
                />
              </Box>
            ) : (
              <Alert severity="info" sx={{ my: 2 }}>
                Select a Permit Holder and a Year to view and update your
                Pumpage Entries.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>

      <Grid container spacing={4}>
        {/* Drought Alert Section */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Current Drought Status
              </Typography>
              {!selectedDroughtZoneNdx ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select a Permit Holder and Year to view the Current Drought
                  Status
                </Alert>
              ) : (
                <CurrentDroughtAlert ids={[selectedDroughtZoneNdx]} />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Placeholder for Another Grid Section */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <PumpageSummary
                permitNdx={selectedPermitNdx}
                year={selectedYear}
                onRefetch={(refetch) => {
                  pumpageSummaryRefetchRef.current = refetch;
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            Edit Pumpage Data - {MONTH_NAMES[selectedMonth] || selectedMonth}
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                fontWeight: "normal",
                mt: 0.5,
              }}
            >
              Current Date: {dayjs().format("MM/DD/YYYY")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMonth && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Input Fields Section */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "primary.main", fontWeight: "bold" }}
                  >
                    Data Entry
                  </Typography>

                  {isLegacyMode(
                    editingValues.rpt_year,
                    editingValues.rpt_month
                  ) ? (
                    <TextField
                      margin="normal"
                      fullWidth
                      label="Total Pumped (Legacy)"
                      value={editingValues.total_pumped ?? ""}
                      onChange={(e) =>
                        setEditingValues({
                          ...editingValues,
                          total_pumped: e.target.value,
                        })
                      }
                      type="number"
                      helperText="Enter the total pumped amount for this month (in gallons) - Legacy mode"
                      required
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <TextField
                      margin="normal"
                      fullWidth
                      label="Current Meter Reading"
                      value={editingValues.meter_reading ?? ""}
                      onChange={(e) =>
                        setEditingValues({
                          ...editingValues,
                          meter_reading: e.target.value,
                        })
                      }
                      type="number"
                      helperText="Enter the current meter reading for this month (in gallons)"
                      required
                      sx={{ mb: 2 }}
                    />
                  )}

                  <TextField
                    margin="normal"
                    fullWidth
                    label="Other Water Sources"
                    value={editingValues.other_water_srcs ?? ""}
                    onChange={(e) =>
                      setEditingValues({
                        ...editingValues,
                        other_water_srcs: e.target.value,
                      })
                    }
                    type="number"
                    helperText="Water from other sources (in gallons)"
                    required
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    margin="normal"
                    fullWidth
                    label="Total Sold/Used"
                    value={editingValues.total_sold ?? ""}
                    onChange={(e) =>
                      setEditingValues({
                        ...editingValues,
                        total_sold: e.target.value,
                      })
                    }
                    type="number"
                    helperText="Total amount sold/used (in gallons)."
                    required
                    sx={{ mb: 2 }}
                  />

                  {(() => {
                    let pumpageAmount;
                    if (
                      isLegacyMode(
                        editingValues.rpt_year,
                        editingValues.rpt_month
                      )
                    ) {
                      pumpageAmount = Number(editingValues.total_pumped) || 0;
                    } else {
                      pumpageAmount = calculateCurrentPumpage(
                        editingValues.meter_reading,
                        editingValues.prev_meter_reading
                      );
                    }
                    const other = Number(editingValues.other_water_srcs) || 0;
                    const sold = Number(editingValues.total_sold) || 0;
                    const totalAvailable = pumpageAmount + other;
                    const lossPercent =
                      totalAvailable === 0
                        ? 0
                        : Math.round(
                            ((totalAvailable - sold) / totalAvailable) *
                              100 *
                              100
                          ) / 100;

                    // Only render the loss explanation field if loss exceeds 15%
                    if (lossPercent > 15) {
                      return (
                        <TextField
                          margin="normal"
                          fullWidth
                          label="Loss Explanation"
                          value={editingValues.loss_explain ?? ""}
                          onChange={(e) =>
                            setEditingValues({
                              ...editingValues,
                              loss_explain: e.target.value,
                            })
                          }
                          multiline
                          rows={3}
                          helperText="Explanation required when loss exceeds 15%"
                          required
                          error={
                            !editingValues.loss_explain ||
                            editingValues.loss_explain.trim() === ""
                          }
                        />
                      );
                    }
                    return null;
                  })()}
                </Grid>

                {/* Calculated Values Section */}
                <Grid item xs={12} md={6}>
                  {/* Previous Data Context */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Previous Data Context</AlertTitle>
                    <Typography variant="body2">
                      <strong>Most Recent Prior Meter Reading:</strong>{" "}
                      {isLegacyMode(
                        editingValues.rpt_year,
                        editingValues.rpt_month
                      ) ? (
                        <span style={{ fontStyle: "italic", color: "#666" }}>
                          Not applicable - this record predates meter reading
                          tracking
                        </span>
                      ) : editingValues.prev_meter_reading !== null &&
                        editingValues.prev_meter_reading !== undefined ? (
                        <>
                          {Number(
                            editingValues.prev_meter_reading
                          ).toLocaleString()}{" "}
                          gallons
                        </>
                      ) : (
                        <span style={{ fontStyle: "italic", color: "#666" }}>
                          Not applicable - at this time, we only recorded
                          pumpage amounts, not meter readings
                        </span>
                      )}
                    </Typography>
                    {editingValues.prev_pumpage !== null &&
                      editingValues.prev_pumpage !== undefined && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Most Recent Prior Pumpage:</strong>{" "}
                          {Number(editingValues.prev_pumpage).toLocaleString()}{" "}
                          gallons
                        </Typography>
                      )}
                  </Alert>

                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "secondary.main", fontWeight: "bold" }}
                  >
                    Calculated Values
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <strong>
                        {isLegacyMode(
                          editingValues.rpt_year,
                          editingValues.rpt_month
                        )
                          ? "Total Pumped (Legacy):"
                          : "Monthly Pumpage:"}
                      </strong>
                      <span>
                        {(() => {
                          if (
                            isLegacyMode(
                              editingValues.rpt_year,
                              editingValues.rpt_month
                            )
                          ) {
                            const totalPumped =
                              Number(editingValues.total_pumped) || 0;
                            return totalPumped > 0
                              ? `${totalPumped.toLocaleString()} gallons`
                              : "---";
                          } else {
                            const calculatedPumpage = calculateCurrentPumpage(
                              editingValues.meter_reading,
                              editingValues.prev_meter_reading
                            );
                            return calculatedPumpage > 0
                              ? `${calculatedPumpage.toLocaleString()} gallons`
                              : "---";
                          }
                        })()}
                      </span>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <strong>Other Sources:</strong>
                      <span>
                        {(() => {
                          const other =
                            Number(editingValues.other_water_srcs) || 0;
                          return other > 0
                            ? `${other.toLocaleString()} gallons`
                            : "---";
                        })()}
                      </span>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <strong>Total Available:</strong>
                      <span>
                        {(() => {
                          let pumpageAmount;
                          if (
                            isLegacyMode(
                              editingValues.rpt_year,
                              editingValues.rpt_month
                            )
                          ) {
                            pumpageAmount =
                              Number(editingValues.total_pumped) || 0;
                          } else {
                            pumpageAmount = calculateCurrentPumpage(
                              editingValues.meter_reading,
                              editingValues.prev_meter_reading
                            );
                          }
                          const other =
                            Number(editingValues.other_water_srcs) || 0;
                          const total = pumpageAmount + other;
                          return total > 0
                            ? `${total.toLocaleString()} gallons`
                            : "---";
                        })()}
                      </span>
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <strong>Total Sold/Used:</strong>
                      <span>
                        {editingValues.total_sold !== "" &&
                        editingValues.total_sold !== null &&
                        editingValues.total_sold !== undefined
                          ? `${Number(
                              editingValues.total_sold
                            ).toLocaleString()} gallons`
                          : "---"}
                      </span>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        color: (() => {
                          let pumpageAmount;
                          if (
                            isLegacyMode(
                              editingValues.rpt_year,
                              editingValues.rpt_month
                            )
                          ) {
                            pumpageAmount =
                              Number(editingValues.total_pumped) || 0;
                          } else {
                            pumpageAmount = calculateCurrentPumpage(
                              editingValues.meter_reading,
                              editingValues.prev_meter_reading
                            );
                          }
                          const other =
                            Number(editingValues.other_water_srcs) || 0;
                          const sold = Number(editingValues.total_sold) || 0;
                          const totalAvailable = pumpageAmount + other;
                          const lossPercent =
                            totalAvailable === 0
                              ? 0
                              : Math.round(
                                  ((totalAvailable - sold) / totalAvailable) *
                                    100
                                );
                          return lossPercent > 15
                            ? "error.main"
                            : "text.primary";
                        })(),
                      }}
                    >
                      <strong>Loss:</strong>
                      <span>
                        {(() => {
                          let pumpageAmount;
                          if (
                            isLegacyMode(
                              editingValues.rpt_year,
                              editingValues.rpt_month
                            )
                          ) {
                            pumpageAmount =
                              Number(editingValues.total_pumped) || 0;
                          } else {
                            pumpageAmount = calculateCurrentPumpage(
                              editingValues.meter_reading,
                              editingValues.prev_meter_reading
                            );
                          }
                          const other =
                            Number(editingValues.other_water_srcs) || 0;
                          const sold =
                            editingValues.total_sold !== "" &&
                            editingValues.total_sold !== null &&
                            editingValues.total_sold !== undefined
                              ? Number(editingValues.total_sold)
                              : null;
                          return sold !== null
                            ? `${(
                                pumpageAmount +
                                other -
                                sold
                              ).toLocaleString()} gallons`
                            : "---";
                        })()}
                      </span>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        color: (() => {
                          let pumpageAmount;
                          if (
                            isLegacyMode(
                              editingValues.rpt_year,
                              editingValues.rpt_month
                            )
                          ) {
                            pumpageAmount =
                              Number(editingValues.total_pumped) || 0;
                          } else {
                            pumpageAmount = calculateCurrentPumpage(
                              editingValues.meter_reading,
                              editingValues.prev_meter_reading
                            );
                          }
                          const other =
                            Number(editingValues.other_water_srcs) || 0;
                          const sold = Number(editingValues.total_sold) || 0;
                          const totalAvailable = pumpageAmount + other;
                          const lossPercent =
                            totalAvailable === 0
                              ? 0
                              : Math.round(
                                  ((totalAvailable - sold) / totalAvailable) *
                                    100
                                );
                          return lossPercent > 15
                            ? "error.main"
                            : "text.primary";
                        })(),
                      }}
                    >
                      <strong>Loss (% of gallons pumped):</strong>
                      <span>
                        {(() => {
                          let pumpageAmount;
                          if (
                            isLegacyMode(
                              editingValues.rpt_year,
                              editingValues.rpt_month
                            )
                          ) {
                            pumpageAmount =
                              Number(editingValues.total_pumped) || 0;
                          } else {
                            pumpageAmount = calculateCurrentPumpage(
                              editingValues.meter_reading,
                              editingValues.prev_meter_reading
                            );
                          }
                          const other =
                            Number(editingValues.other_water_srcs) || 0;
                          const sold =
                            editingValues.total_sold !== "" &&
                            editingValues.total_sold !== null &&
                            editingValues.total_sold !== undefined
                              ? Number(editingValues.total_sold)
                              : null;
                          const totalAvailable = pumpageAmount + other;
                          if (sold === null) return "---";
                          return totalAvailable === 0
                            ? "0"
                            : Math.round(
                                ((totalAvailable - sold) / totalAvailable) *
                                  100 *
                                  100
                              ) / 100;
                        })()}
                        %
                      </span>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={saveSingleMonthMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDialogData}
            variant="contained"
            color="secondary"
            disabled={saveSingleMonthMutation.isLoading}
          >
            {saveSingleMonthMutation.isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <BaselineDialog
        open={baselineDlgOpen}
        onClose={() => {
          setBaselineDlgOpen(false);
          setBaselineDismissed(true);
        }}
        permitNdx={selectedPermitNdx}
        prevPumpageInfo={latestPrevPumpageInfo}
        onSaved={async () => {
          await pumpageEntriesQuery.refetch();
          if (pumpageSummaryRefetchRef.current) {
            pumpageSummaryRefetchRef.current();
          }
        }}
      />
    </Box>
  );
}
